import L from 'leaflet';
import simpleheat from 'simpleheat';
import { DENSITY_RADIUS, DENSITY_BLUR, DensityCellData } from '../../core/density';

interface HeatAppearance {
  opacity: number;
  radius: number;
  blur: number;
  maximum: number;
  gradient: Record<number, string>;
}

/** One reusable viewport canvas. Pan/zoom reuse its pixels until a settled redraw. */
export class DensityHeatLayer extends L.Layer {
  private attachedMap?: L.Map;
  private canvas?: HTMLCanvasElement;
  private heat?: ReturnType<typeof simpleheat>;
  private frame: number | null = null;
  private moving = false;
  private cells: readonly DensityCellData[] = [];
  private appearance?: HeatAppearance;

  override onAdd(map: L.Map): this {
    this.attachedMap = map;
    const pane = map.getPane('densityPane') ?? map.createPane('densityPane');
    pane.style.zIndex = '350';
    pane.style.pointerEvents = 'none';
    this.canvas = L.DomUtil.create('canvas', 'leaflet-layer leaflet-zoom-hide', pane);
    this.canvas.setAttribute('aria-hidden', 'true');
    this.heat = simpleheat(this.canvas).radius(
      this.appearance?.radius ?? DENSITY_RADIUS, this.appearance?.blur ?? DENSITY_BLUR,
    );
    map.on('movestart zoomstart', this.suspend, this);
    map.on('moveend zoomend resize', this.settle, this);
    this.redraw();
    return this;
  }

  override onRemove(map: L.Map): this {
    map.off('movestart zoomstart', this.suspend, this);
    map.off('moveend zoomend resize', this.settle, this);
    this.clear();
    this.canvas?.remove();
    this.canvas = undefined;
    this.heat = undefined;
    this.attachedMap = undefined;
    this.moving = false;
    return this;
  }

  setData(cells: readonly DensityCellData[], appearance: HeatAppearance): void {
    const kernelChanged = appearance.radius !== this.appearance?.radius || appearance.blur !== this.appearance?.blur;
    if (kernelChanged) this.heat?.radius(appearance.radius, appearance.blur);
    const changed = kernelChanged || cells !== this.cells || appearance.maximum !== this.appearance?.maximum ||
      appearance.gradient !== this.appearance?.gradient;
    this.cells = cells;
    this.appearance = appearance;
    if (this.canvas) this.canvas.style.opacity = String(appearance.opacity / 100);
    if (changed) this.redraw();
  }

  clear(): void {
    this.cells = [];
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.canvas?.getContext('2d')?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  private suspend(): void {
    this.moving = true;
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  private settle(): void { this.moving = false; this.redraw(); }

  private redraw(): void {
    if (!this.attachedMap || this.moving || this.frame !== null) return;
    this.frame = requestAnimationFrame(() => { this.frame = null; this.draw(); });
  }

  private draw(): void {
    const map = this.attachedMap;
    const canvas = this.canvas;
    const heat = this.heat;
    const appearance = this.appearance;
    if (!map || !canvas || !heat || !appearance) return;
    const size = map.getSize();
    const border = appearance.radius + appearance.blur;
    const width = size.x + border * 2;
    const height = size.y + border * 2;
    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
      heat.resize();
    }
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([-border, -border]));
    canvas.style.opacity = String(appearance.opacity / 100);
    const world = 256 * 2 ** map.getZoom();
    const center = map.getCenter().lng;
    const points: [number, number, number][] = [];
    for (const cell of this.cells) {
      const lng = cell.lng + 360 * Math.round((center - cell.lng) / 360);
      const point = map.latLngToContainerPoint([cell.lat, lng]);
      if (point.y < -border || point.y > size.y + border) continue;
      for (let copy = Math.ceil((-border - point.x) / world); copy <= Math.floor((size.x + border - point.x) / world); copy++) {
        points.push([point.x + copy * world + border, point.y + border, Math.min(cell.count, appearance.maximum)]);
      }
    }
    heat.gradient(appearance.gradient).max(appearance.maximum).data(points).draw(0);
  }
}
